import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { v1Client } from '@renderer/chat/api/v1Client'
import type { ApiBriefData, MeetingBrief } from './types'
import { meetingBriefQueryKey, meetingBriefRefetchInterval, meetingBriefStaleTime } from './meeting-brief-query'
import { buildBriefBullets, buildBriefLinkPreviews } from './link-previews'

interface MeetingBriefState {
  data: MeetingBrief | null
  isLoading: boolean
  isGenerating: boolean
  isFailed: boolean
  /** A regeneration is in flight. `data` still holds the previous brief. */
  isRegenerating: boolean
  /** Chat producing the brief — the in-flight regeneration when one is running. */
  conversationId: string | null
  error: Error | null
}

interface ApiResponse {
  success: boolean
  data: ApiBriefData
}

async function fetchBrief(calendarEventId: string, token: string): Promise<ApiBriefData | null> {
  // The brief route is not in the generated openapi paths yet — swap the
  // cast for a typed call once `chat/types/backend-openapi.d.ts` is
  // regenerated to include `/calendar-events/{id}/brief`.
  const response = (await (v1Client as any).GET('/calendar-events/{id}/brief', {
    params: { path: { id: calendarEventId }, query: { version: 'v2' } },
    headers: { Authorization: `Bearer ${token}` },
  })) as { response?: { status?: number }; error?: unknown; data?: ApiResponse }
  if (response.response?.status === 404) return null
  if (response.error) {
    const message = typeof response.error === 'string' ? response.error : 'Brief fetch failed'
    throw new Error(message)
  }
  return response.data?.data ?? null
}

export function useMeetingBrief(
  calendarEventId: string | undefined,
  options?: { enabled?: boolean; pollWhilePending?: boolean; presenceConversationId?: string | null },
): MeetingBriefState {
  const { getAuthToken } = useAuth()
  const enabled = (options?.enabled ?? true) && !!calendarEventId
  const presence = {
    pollWhilePending: options?.pollWhilePending ?? false,
    presenceConversationId: options?.presenceConversationId ?? null,
  }
  const query = useQuery({
    queryKey: meetingBriefQueryKey(calendarEventId ?? ''),
    queryFn: async () => {
      if (!calendarEventId) return null
      const token = await getAuthToken()
      if (!token) throw new Error('No highlight token found')
      return fetchBrief(calendarEventId, token)
    },
    enabled,
    staleTime: (q) => meetingBriefStaleTime(q.state.data, presence),
    // Overlay often sits unfocused while the user is in the meeting app;
    // without this, generating polls pause and the ready brief never lands.
    refetchIntervalInBackground: true,
    refetchInterval: (q) => meetingBriefRefetchInterval(q.state.data, presence),
  })

  const apiData = query.data ?? null
  const queryError = (query.error as Error | null) ?? null

  return useMemo<MeetingBriefState>(() => {
    if (!apiData) {
      return {
        data: null,
        isLoading: query.isLoading,
        isGenerating: false,
        isFailed: false,
        isRegenerating: false,
        conversationId: null,
        error: queryError,
      }
    }

    if (apiData.status === 'ready') {
      const unstructured =
        'brief' in apiData
          ? { brief: apiData.brief, relevantLinks: apiData.relevantLinks }
          : apiData.content.unstructured
      return {
        data: {
          brief: unstructured.brief,
          relevantLinks: unstructured.relevantLinks,
          conversationId: apiData.conversationId,
          // v1 rows carry no references: their links render without a card and
          // their body falls back to the flattened markdown.
          ...('content' in apiData
            ? {
                linkPreviews: buildBriefLinkPreviews(apiData.content),
                structure: {
                  headline: apiData.content.brief.headline,
                  bullets: buildBriefBullets(apiData.content, unstructured.relevantLinks),
                },
              }
            : {}),
        },
        isLoading: false,
        isGenerating: false,
        isFailed: false,
        isRegenerating: !!apiData.regenerationConversationId,
        // The save happens in the regeneration chat, not the ready brief's —
        // the save watcher must follow the in-flight conversation.
        conversationId: apiData.regenerationConversationId ?? apiData.conversationId,
        error: null,
      }
    }

    return {
      data: null,
      isLoading: false,
      isGenerating: apiData.status === 'generating',
      isFailed: apiData.status === 'failed',
      isRegenerating: false,
      conversationId: apiData.conversationId,
      error: null,
    }
  }, [apiData, query.isLoading, queryError])
}
